import React, { useContext, useState } from 'react';
import {
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  Icons,
  Radio,
  RadioGroup,
  SimpleDialog,
  Stack,
  TextArea,
  Typography,
} from '@nextiva/next-ui-components';
import { Controller } from 'react-hook-form-new';
import { FeatureContext } from 'components/AccessResolvers/featureContext';
import { BlueMessage, RadioLabel, StyledIcon } from '../Steps.styled';
import { useWizardContextV3 } from '../WizardContextV3/WizardContextV3';
import {
  BRAND_REGISTRATION_FIELDS,
  CompanyTypeInfo,
  FIELD_NAMES,
  MESSAGING_PLAN_FIELDS,
} from '../constants';
import { Body2Typo, OverlineTypo, SubtitleTypo } from '../WizardV3.styled';
import { SmsSubUseCases } from './SmsSubUseCases/SmsSubUseCases';

const dataTestId = 'PlatformAdminUI-MessagingPlan';
export function MessagingPlan() {
  const {
    control,
    formState: { errors },
    getValues,
    watch,
  } = useWizardContextV3();
  const messagePlanExplanation = watch(
    MESSAGING_PLAN_FIELDS.CAMPAIGN_DESCRIPTION,
  );
  const isSoleProprietor =
    getValues(BRAND_REGISTRATION_FIELDS.COMPANY_TYPE) ===
    CompanyTypeInfo[4].value;

  const {
    featureState: { smsEnablementGenerateContent, smsUseCases },
  } = useContext(FeatureContext);
  const [modalState, setModalState] = useState(false);
  const [writeOwnExplanation, setWriteOwnExplanation] = useState(false);
  const [internalUsers, setInternalUsers] = useState(false);
  const [externalUsers, setExternalUsers] = useState(false);
  const [patients, setPatients] = useState(false);
  const [investors, setInvestors] = useState(false);
  const [others, setOthers] = useState(false);

  return (
    <div>
      {!isSoleProprietor && (
        <>
          <SubtitleTypo mb={2}>
            How many phone numbers are you going to use for messaging?
          </SubtitleTypo>
          <Controller
            control={control}
            name={MESSAGING_PLAN_FIELDS.NUMBER_OF_LINES}
            rules={{ required: true }}
            render={({ field: { onChange, onBlur, value = '' } }) => {
              return (
                <RadioGroup
                  aria-labelledby="messaging-label"
                  id="messaging"
                  sx={{ mb: '48px' }}
                  data-testid={`${dataTestId}-messaging`}
                  value={value}
                  onChange={(e) => {
                    onChange(e.target.value);
                  }}
                  onBlur={onBlur}
                >
                  <Stack mb={2}>
                    <FormControlLabel
                      control={
                        <Radio checked={value === '40' || value === 40} />
                      }
                      label={<Body2Typo>40 numbers or less</Body2Typo>}
                      value={40}
                      sx={{
                        color: errors.numberOfLines ? 'error.dark' : undefined,
                      }}
                    />
                  </Stack>
                  <FormControlLabel
                    control={<Radio checked={value === '41' || value === 41} />}
                    label={<Body2Typo>41 numbers or more</Body2Typo>}
                    value={41}
                    sx={{
                      color: errors.numberOfLines ? 'error.dark' : undefined,
                    }}
                  />
                  {!!errors.numberOfLines && (
                    <Typography
                      variant="caption"
                      color="error.dark"
                      sx={{ mt: '4px' }}
                    >
                      Please select one option.
                    </Typography>
                  )}
                </RadioGroup>
              );
            }}
          />
          <Divider />
        </>
      )}
      <SubtitleTypo mt={4} mb={2}>
        Please explain how you plan to use text messaging
        <IconButton
          aria-label="message-plan-tooltip"
          onClick={() => setModalState(true)}
          data-testid={`${dataTestId}-Modal-HelpIcon`}
        >
          <StyledIcon icon={Icons.info} />
        </IconButton>
      </SubtitleTypo>
      <SimpleDialog
        open={modalState}
        title="Text messaging usage"
        confirmText="Done"
        confirmButtonProps={{
          color: 'primary',
          onClick: () => setModalState(false),
          'data-testid': 'PlatformAdminUI-MessagingPlan-DoneButton',
        }}
      >
        <div>
          <Body2Typo width="347px" mb={1}>
            Describe in detail how you plan to use text messaging.
          </Body2Typo>
          <Stack ml={2}>
            <ul>
              <li>
                <Body2Typo width="327px">
                  Will it be used to reply to customers when they initiate
                  messaging?
                </Body2Typo>
              </li>
              <li>
                <Body2Typo width="327px">
                  Will it be used for internal employee messaging?
                </Body2Typo>
              </li>
              <li>
                <Body2Typo width="327px">
                  Will it be used for customer follow up via messaging?
                </Body2Typo>
              </li>
              <li>
                <Body2Typo width="327px">
                  Will it be used for sales and promotional messaging?
                </Body2Typo>
              </li>
            </ul>
          </Stack>
          <Body2Typo width="347px" my={1}>
            Please avoid general statements and be specific.
          </Body2Typo>
          <Body2Typo>Include-</Body2Typo>
          <Stack ml={2}>
            <ol>
              <li>
                <Body2Typo>Who is SMS used between,</Body2Typo>
              </li>
              <li>
                <Body2Typo>Who initiates SMS,</Body2Typo>
              </li>
              <li>
                <Body2Typo width="327px" mb={3}>
                  Name important example topics (scheduling, follow up,
                  conversational, etc.)
                </Body2Typo>
              </li>
            </ol>
          </Stack>
        </div>
      </SimpleDialog>
      {smsEnablementGenerateContent && (
        <>
          <RadioGroup
            aria-labelledby="obtained-label"
            id="obtain"
            onChange={(e) => setWriteOwnExplanation(e.target.value === 'true')}
            data-testid={`${dataTestId}-Obtained`}
            defaultValue="false"
          >
            <Stack direction="row" mb={2}>
              <RadioLabel
                label="Let us help create your explanation"
                value="false"
              />
              <BlueMessage variant="caption">Recommended</BlueMessage>
            </Stack>
            <RadioLabel
              label="Write your explanation without help"
              value="true"
            />
          </RadioGroup>
          <Typography
            variant="subtitle3"
            fontWeight={500}
            component="div"
            my={2}
            color="#0B1029"
          >
            Who are the target recipients of your messages?
          </Typography>
          <Body2Typo component="div">Select all that applies</Body2Typo>
          <Stack>
            <FormControlLabel
              sx={{ mt: '16px' }}
              control={
                <Checkbox
                  disableRipple
                  onChange={(event) => setInternalUsers(event.target.checked)}
                  checked={internalUsers}
                  disabled={writeOwnExplanation}
                  data-testid={`${dataTestId}-InternalUsers-Checkbox`}
                />
              }
              label={<Body2Typo ml={1}>Internal users</Body2Typo>}
            />
            <FormControlLabel
              sx={{ mt: '16px' }}
              control={
                <Checkbox
                  disableRipple
                  onChange={(event) => setExternalUsers(event.target.checked)}
                  checked={externalUsers}
                  disabled={writeOwnExplanation}
                  data-testid={`${dataTestId}-ExternalUsers-Checkbox`}
                />
              }
              label={<Body2Typo ml={1}>External users</Body2Typo>}
            />
            <FormControlLabel
              sx={{ mt: '16px' }}
              control={
                <Checkbox
                  disableRipple
                  onChange={(event) => setPatients(event.target.checked)}
                  checked={patients}
                  disabled={writeOwnExplanation}
                  data-testid={`${dataTestId}-Patients-Checkbox`}
                />
              }
              label={<Body2Typo ml={1}>Patients</Body2Typo>}
            />
            <FormControlLabel
              sx={{ mt: '16px' }}
              control={
                <Checkbox
                  disableRipple
                  onChange={(event) => setInvestors(event.target.checked)}
                  checked={investors}
                  disabled={writeOwnExplanation}
                  data-testid={`${dataTestId}-Investors-Checkbox`}
                />
              }
              label={<Body2Typo ml={1}>Investors</Body2Typo>}
            />
            <FormControlLabel
              sx={{ mt: '16px' }}
              control={
                <Checkbox
                  disableRipple
                  onChange={(event) => setOthers(event.target.checked)}
                  checked={others}
                  disabled={writeOwnExplanation}
                  data-testid={`${dataTestId}-Other-Checkbox`}
                />
              }
              label={<Body2Typo ml={1}>Other (edit later)</Body2Typo>}
            />
          </Stack>
          <Typography
            variant="subtitle3"
            fontWeight={500}
            component="div"
            my={2}
            color="#0B1029"
          >
            What is the intended use or purpose for messaging?
          </Typography>
          <Stack width="497px">
            <Controller
              control={control}
              name={MESSAGING_PLAN_FIELDS.MESSAGE_PLAN}
              rules={{ maxLength: 500 }}
              render={({ field: { onChange, onBlur, value } }) => {
                const stringValue = value as string;
                return (
                  <TextArea
                    value={stringValue || ''}
                    onChange={onChange}
                    onBlur={onBlur}
                    charLimit={500}
                    maxRows={5}
                    disabled={writeOwnExplanation}
                    error={!!errors.messagePlan}
                    label={
                      <OverlineTypo>
                        {FIELD_NAMES[MESSAGING_PLAN_FIELDS.MESSAGE_PLAN]}
                      </OverlineTypo>
                    }
                    placeholder="Example: Casual conversation, Medical advises, Investment marketing, Meetings etc."
                    data-testid={`${dataTestId}-MessagingPlan-Textarea`}
                  />
                );
              }}
            />
          </Stack>
          <Stack width="208px">
            <Button
              disabled={
                writeOwnExplanation ||
                !(
                  internalUsers ||
                  externalUsers ||
                  patients ||
                  investors ||
                  others
                ) ||
                !messagePlanExplanation
              }
              color="primary"
              variant="outlined"
            >
              Generate explanation
            </Button>
          </Stack>
        </>
      )}
      <Controller
        control={control}
        name={MESSAGING_PLAN_FIELDS.CAMPAIGN_DESCRIPTION}
        rules={{ required: true, minLength: 40, maxLength: 1024 }}
        render={({ field: { onChange, onBlur, value } }) => {
          const stringValue = value as string;
          return (
            <TextArea
              value={stringValue || ''}
              onChange={onChange}
              onBlur={onBlur}
              charLimit={1024}
              maxRows={5}
              required
              sx={{ width: '497px', mt: '16px', mb: '48px' }}
              error={!!errors.campaignDescription}
              label={
                <OverlineTypo>
                  {FIELD_NAMES[MESSAGING_PLAN_FIELDS.CAMPAIGN_DESCRIPTION]}
                </OverlineTypo>
              }
              placeholder="Example: Texting will be used to communicate with external users. This includes topics such as casual conversation, meetings. Customers initiate conversation after getting our number from our office/our site."
              helperText="Minimum 40 characters"
              data-testid={`${dataTestId}-MessagingPlan-Textarea`}
            />
          );
        }}
      />
      {smsUseCases && <SmsSubUseCases />}
    </div>
  );
}
